import type {
  FolderCoverageInput,
  FolderCoverageProvider,
  FolderCoverageReport,
} from './types';

/**
 * Global provider registry. Domains register exactly one provider
 * per `folder.domain` value via their domain bootstrap code
 * (`registerNetworkDomain`, `registerProcessDomain`, …).
 *
 * Unknown domains return a minimal "inventory only" report that
 * just surfaces any connected sources without claiming fidelity.
 */
const providers = new Map<string, FolderCoverageProvider>();

export function registerFolderCoverageProvider(provider: FolderCoverageProvider): void {
  providers.set(provider.domain, provider);
}

export function getFolderCoverageProvider(
  domain: string | null | undefined,
): FolderCoverageProvider | null {
  if (!domain) return null;
  return providers.get(domain) ?? null;
}

export function computeFolderCoverage(
  domain: string | null | undefined,
  input: FolderCoverageInput,
): FolderCoverageReport {
  const provider = getFolderCoverageProvider(domain);
  if (provider) return provider.compute(input);
  return fallbackReport(domain ?? 'unknown', input);
}

function fallbackReport(domain: string, input: FolderCoverageInput): FolderCoverageReport {
  // No provider registered — we still want the UI to render a
  // useful state rather than blow up. Emit a single "sources"
  // layer that just counts connections.
  const sourceCount = input.sources.length;
  return {
    domain,
    layers: [
      {
        id: 'sources',
        label: 'Sources',
        description: 'Connected data sources feeding this folder.',
        ratio: sourceCount > 0 ? 1 : 0,
        filled: sourceCount,
        total: sourceCount > 0 ? sourceCount : 1,
        upsell:
          sourceCount === 0
            ? {
                connectorId: 'any',
                headline: 'No sources connected',
                blurb:
                  'Add a source to populate this folder. The dashboard picker lists every format the folder type accepts.',
              }
            : undefined,
      },
    ],
    overall: sourceCount > 0 ? 1 : 0,
    resourceCount: sourceCount,
  };
}
